import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, ArrowUpToLine } from "lucide-react";

/**
 * Admin "Screen Order" panel.
 * Lets the admin pick a screen (Browse, every Home row, every genre page) and
 * reorder its video list using Move up / Move down / Move to top. Each click
 * saves immediately; on failure the previous order is restored and a toast
 * is shown.
 * With `only` (a screen key such as "home:dan_picks") it is fixed on that one screen, with its own title and note,
 * and has no pull-down. Admin uses that for the Featured Videos and Dan The Man's Picks sections.
 */
export default function ScreenOrderManager({ only = null, title = "Screen Order", note = null }) {
  const [screens, setScreens] = useState([]);
  const [screenKey, setScreenKey] = useState(only || "");
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load dropdown contents (Browse + Home rows + genres)
  useEffect(() => {
    if (only) return;
    api.get("/admin/screens").then((r) => {
      setScreens(r.data);
      if (r.data.length && !screenKey) setScreenKey(r.data[0].key);
    }).catch(() => toast.error("Could not load screen list"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVideos = useCallback((key) => {
    if (!key) return;
    setLoading(true);
    api.get(`/admin/screen-videos/${encodeURIComponent(key)}`)
      .then((r) => setVideos(r.data))
      .catch(() => toast.error("Could not load videos for this screen"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadVideos(screenKey); }, [screenKey, loadVideos]);

  const saveOrder = useCallback(async (nextList, prevList) => {
    setVideos(nextList); // optimistic
    try {
      await api.put(`/admin/screen-order/${encodeURIComponent(screenKey)}`, {
        video_ids: nextList.map((v) => v.id),
      });
    } catch (e) {
      setVideos(prevList); // rollback
      toast.error(e.response?.data?.detail || "Save failed — order restored");
    }
  }, [screenKey]);

  const moveUp = (idx) => {
    if (idx <= 0) return;
    const next = videos.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    saveOrder(next, videos);
  };
  const moveDown = (idx) => {
    if (idx >= videos.length - 1) return;
    const next = videos.slice();
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    saveOrder(next, videos);
  };
  const moveTop = (idx) => {
    if (idx <= 0) return;
    const next = videos.slice();
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    saveOrder(next, videos);
  };

  const currentLabel = useMemo(
    () => screens.find((s) => s.key === screenKey)?.label || screenKey,
    [screens, screenKey],
  );

  return (
    <div data-testid={only ? `screen-order-${only}` : "screen-order-manager"}>
      <h2 className="font-display text-2xl font-bold mb-4">{title}</h2>
      {only && note && <p className="text-sm text-zinc-400 mb-3" data-testid="screen-order-note">{note}</p>}
      {!only && <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-3 mb-3">
        <label className="text-[10px] font-mono-ui uppercase tracking-widest text-zinc-500 block mb-1">Pick a screen</label>
        <select
          data-testid="screen-order-select"
          value={screenKey}
          onChange={(e) => setScreenKey(e.target.value)}
          style={{ colorScheme: "dark" }}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50"
        >
          {screens.map((s) => <option key={s.key} value={s.key} style={{ backgroundColor: "#1A1A26", color: "#FFFFFF" }}>{s.label}</option>)}
        </select>
        <div className="text-[11px] text-zinc-500 mt-2">Order shown below matches what visitors see on <span className="text-zinc-300">{currentLabel}</span>.</div>
      </div>}

      <div className="space-y-2 max-h-[480px] overflow-y-auto pr-2">
        {loading && <div className="text-zinc-500 text-sm py-4 text-center">Loading…</div>}
        {!loading && videos.length === 0 && (
          <div className="text-zinc-500 text-sm py-4 text-center">{only ? "No videos here yet." : "No videos on this screen yet."}</div>
        )}
        {!loading && videos.map((v, idx) => (
          <div key={v.id} className="flex items-center gap-2 p-2 bg-zinc-900/60 border border-white/10 rounded-lg" data-testid={`screen-order-row-${v.id}`}>
            <div className="w-7 shrink-0 text-right text-xs font-mono-ui text-zinc-500">{idx + 1}</div>
            <img src={v.cover_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{v.title}</div>
              <div className="text-xs text-zinc-500 truncate">{v.artist}</div>
            </div>
            <button
              data-testid={`screen-order-top-${v.id}`}
              onClick={() => moveTop(idx)}
              disabled={idx === 0}
              title="Move to top"
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ArrowUpToLine className="w-4 h-4" />
            </button>
            <button
              data-testid={`screen-order-up-${v.id}`}
              onClick={() => moveUp(idx)}
              disabled={idx === 0}
              title="Move up"
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
            <button
              data-testid={`screen-order-down-${v.id}`}
              onClick={() => moveDown(idx)}
              disabled={idx === videos.length - 1}
              title="Move down"
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
